"use client"

import { addToCart } from "@lib/data/cart"
import { listProductsBySkus, ProductBySku } from "@lib/data/products"
import { getProductPrice } from "@lib/util/get-product-price"
import { convertToLocale } from "@lib/util/money"
import { getUpsellSkus, orderProductsBySkus } from "@lib/util/related-products"
import { HttpTypes } from "@medusajs/types"
import { Button, Text } from "@modules/common/components/ui"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Modal from "@modules/common/components/modal"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { useOptionSelection } from "@modules/products/hooks/use-option-selection"
import Image from "next/image"
import { useParams } from "next/navigation"
import { useState } from "react"

const MAX_CHAIN_ITEMS = 5

type QuickAddModalProps = {
  product: HttpTypes.StoreProduct
  regionId: string
  isOpen: boolean
  close: () => void
}

type Step = "select" | "chain" | "done"

// Minimal rapid add flow: confirm the product (with option pills when
// needed), then walk the matched cross-sell SKUs one at a time as
// yes/no offers (shroud -> fans -> relay -> accessories).
export default function QuickAddModal({
  product,
  regionId,
  isOpen,
  close,
}: QuickAddModalProps) {
  const countryCode = useParams().countryCode as string

  const [step, setStep] = useState<Step>("select")
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chainItems, setChainItems] = useState<ProductBySku[]>([])
  const [chainIndex, setChainIndex] = useState(0)

  const {
    options,
    setOptionValue,
    selectedVariant,
    visibleOptionValues,
    isValidVariant,
    inStock,
  } = useOptionSelection(product)

  const { variantPrice, cheapestPrice } = getProductPrice({
    product,
    variantId: selectedVariant?.id,
  })
  const selectedPrice = variantPrice || cheapestPrice

  const hasOptions = (product.variants?.length ?? 0) > 1

  const handleConfirm = async () => {
    if (!selectedVariant?.id) return

    setError(null)
    setIsBusy(true)

    try {
      await addToCart({
        variantId: selectedVariant.id,
        quantity: 1,
        countryCode,
      })

      // Chain the matched cross-sell SKUs, skipping out-of-stock items,
      // the product itself and the variant just added.
      const skus = getUpsellSkus(product)
      const resolved = skus.length
        ? await listProductsBySkus(skus, regionId)
        : []
      const chain = orderProductsBySkus(skus, resolved)
        .filter(
          (p) =>
            p.in_stock &&
            p.id !== product.id &&
            p.variant_id !== selectedVariant.id
        )
        .slice(0, MAX_CHAIN_ITEMS)

      if (chain.length) {
        setChainItems(chain)
        setChainIndex(0)
        setStep("chain")
      } else {
        setStep("done")
      }
    } catch {
      setError("Couldn't add to cart — please try again")
    } finally {
      setIsBusy(false)
    }
  }

  const advanceChain = () => {
    if (chainIndex + 1 < chainItems.length) {
      setChainIndex(chainIndex + 1)
    } else {
      setStep("done")
    }
  }

  const handleChainAdd = async (item: ProductBySku) => {
    setError(null)
    setIsBusy(true)

    try {
      await addToCart({
        variantId: item.variant_id,
        quantity: 1,
        countryCode,
      })
      advanceChain()
    } catch {
      setError("Couldn't add to cart — please try again")
    } finally {
      setIsBusy(false)
    }
  }

  const currentChainItem = chainItems[chainIndex]

  return (
    <Modal isOpen={isOpen} close={close} size="small" data-testid="quick-add-modal">
      <Modal.Title>Quick Add</Modal.Title>

      {step === "select" && (
        <div className="flex flex-col gap-y-4 pt-4">
          <div className="flex items-center gap-x-4">
            <div className="relative w-16 h-16 shrink-0 rounded-md bg-white border border-gray-200 overflow-hidden">
              {product.thumbnail && (
                <Image
                  src={product.thumbnail}
                  alt={product.title ?? "Product thumbnail"}
                  className="object-contain object-center p-1"
                  sizes="64px"
                  fill
                />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <Text className="text-small-regular text-ui-fg-base">
                {product.title}
              </Text>
              {selectedPrice && (
                <Text className="text-small-regular font-semibold text-ui-fg-base">
                  {!selectedVariant && hasOptions ? "From " : ""}
                  {selectedPrice.calculated_price}
                </Text>
              )}
            </div>
          </div>
          {hasOptions && (
            <div className="flex flex-col gap-y-4">
              {(product.options || []).map((option) => (
                <div key={option.id}>
                  <OptionSelect
                    option={option}
                    current={options[option.id]}
                    updateOption={setOptionValue}
                    title={option.title ?? ""}
                    visibleValues={visibleOptionValues[option.id]}
                    disabled={isBusy}
                    data-testid="quick-add-options"
                  />
                </div>
              ))}
            </div>
          )}
          <Button
            onClick={handleConfirm}
            disabled={!inStock || !selectedVariant || isBusy || !isValidVariant}
            variant="primary"
            className="w-full h-10"
            isLoading={isBusy}
            data-testid="quick-add-confirm-button"
          >
            {!selectedVariant && hasOptions
              ? "Select options"
              : selectedVariant && !inStock
              ? "Out of stock"
              : `Add to cart${
                  selectedPrice ? ` — ${selectedPrice.calculated_price}` : ""
                }`}
          </Button>
          {error && (
            <Text role="alert" className="text-small-regular text-red-600">
              {error}
            </Text>
          )}
        </div>
      )}

      {step === "chain" && currentChainItem && (
        <div className="flex flex-col gap-y-4 pt-4" data-testid="quick-add-chain">
          <Text role="status" className="text-small-regular text-ui-fg-subtle">
            Added. Want the matched part that goes with it?
          </Text>
          <div className="flex items-center gap-x-4">
            <div className="relative w-16 h-16 shrink-0 rounded-md bg-white border border-gray-200 overflow-hidden">
              {currentChainItem.thumbnail && (
                <Image
                  src={currentChainItem.thumbnail}
                  alt={currentChainItem.title}
                  className="object-contain object-center p-1"
                  sizes="64px"
                  fill
                />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <Text className="text-small-regular text-ui-fg-base">
                {currentChainItem.title}
              </Text>
              {currentChainItem.price != null &&
                currentChainItem.currency_code && (
                  <Text className="text-small-regular font-semibold text-ui-fg-base">
                    {convertToLocale({
                      amount: currentChainItem.price,
                      currency_code: currentChainItem.currency_code,
                    })}
                  </Text>
                )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Button
              onClick={() => handleChainAdd(currentChainItem)}
              variant="primary"
              className="w-full"
              isLoading={isBusy}
              data-testid="quick-add-chain-yes"
            >
              Yes, add it
            </Button>
            <Button
              onClick={advanceChain}
              variant="secondary"
              className="w-full"
              disabled={isBusy}
              data-testid="quick-add-chain-no"
            >
              No thanks
            </Button>
          </div>
          {error && (
            <Text role="alert" className="text-small-regular text-red-600">
              {error}
            </Text>
          )}
          <Text className="text-xsmall-regular text-ui-fg-muted text-center">
            {chainIndex + 1} of {chainItems.length}
          </Text>
        </div>
      )}

      {step === "done" && (
        <div
          className="flex flex-col items-center gap-y-4 pt-6 pb-2"
          data-testid="quick-add-done"
        >
          <Text role="status" className="text-base-regular text-ui-fg-base text-center">
            All set — view cart or keep shopping
          </Text>
          <div className="flex items-center gap-x-4">
            <LocalizedClientLink href="/cart">
              <Button variant="primary" size="small" data-testid="quick-add-view-cart">
                View cart
              </Button>
            </LocalizedClientLink>
            <Button
              variant="secondary"
              size="small"
              onClick={close}
              data-testid="quick-add-keep-shopping"
            >
              Keep shopping
            </Button>
          </div>
        </div>
      )}

    </Modal>
  )
}
